import * as React from 'react';
import styled from "styled-components";
import VariantCard from "./VariantCard";
import { Button, Typography, Message } from '@arco-design/web-react';
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { PostApi } from "../../Api/AuthenticatedApi";

// A single email variant in the campaign's Signup Email list. Mirrors
// FormVariantCard: active variants are a solid card with a green accent stripe;
// paused ones are hatched, dashed and muted on the same card surface (see
// VariantCard).

const { Title, Text } = Typography;

// Card (active vs paused look) is shared with the other variant list.
const Card = VariantCard;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const StatusPill = styled.span`
  flex: none;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  padding: 2px 9px;
  border-radius: 999px;
  color: ${(p) => (p.$active ? "rgb(var(--green-6))" : "var(--color-text-3)")};
  background: ${(p) => (p.$active ? "rgb(var(--green-1))" : "var(--color-fill-2)")};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 8px;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
`;

const metaStyle = { color: "var(--color-text-3)", fontSize: 12 };
const Ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

function formatCreated(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function MailVariantCard(props) {
    const { item, campaignId, onChanged } = props;
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const active = !!item.should_run;

    // Clone = create a new (paused) email variant from this one's content. No
    // backend duplicate endpoint for campaign mail variants, so POST a copy.
    const clone = () => {
        PostApi(getAccessTokenSilently, `/api/campaigns/${campaignId}/email/variants/`,
            () => { Message.success("Email cloned"); onChanged && onChanged(); },
            () => {},
            () => Message.error("Failed to clone email"),
            {
                name: (item.name || "Untitled email") + " (copy)",
                should_run: false,
                mail_subject_template: item.mail_subject_template,
                mail_html_template: item.mail_html_template,
                mail_text_template: item.mail_text_template,
                editor_design: item.editor_design,
            });
    };

    return (
        <Card $active={active}>
            <Header>
                <Title heading={6} style={{ margin: 0, minWidth: 0, ...Ellipsis }}>
                    {item.name || "Untitled email"}
                </Title>
                <StatusPill $active={active}>{active ? "Active" : "Paused"}</StatusPill>
            </Header>

            <Meta>
                <Text style={metaStyle}>Sent: {item.sent || 0}</Text>
                <Text style={metaStyle}>Opens: {item.opens || 0}</Text>
                <Text style={metaStyle}>Clicks: {item.conversions || 0}</Text>
                <Text style={metaStyle}>Unsubscribes: {item.unsubscribes || 0}</Text>
            </Meta>
            <Text style={{ ...metaStyle, display: "block", marginTop: 6 }}>
                Created {formatCreated(item.created_date)}
            </Text>

            <CardActions>
                <Button
                    size="small"
                    type="text"
                    onClick={() => navigate(`/signups/${campaignId}/edit/mail/${item.id}`)}
                >
                    Edit
                </Button>
                <Button size="small" type="text" onClick={clone}>Clone</Button>
            </CardActions>
        </Card>
    );
}
